import React from "react";
import AutoIntl from "@au/core/lib/components/elements/AutoIntl";
import AuDropDown from "@au/core/lib/components/elements/AuDropDown";
import AuToggle, { TOGGLE_SIZE_SMALL } from "@au/core/lib/components/elements/AuToggle";
import { wrapActionWithTracking } from "../../../utils/analyticsHelpers";
import { BUTTON_TYPE_SECONDARY } from "@au/core/lib/components/elements/AuButton";
import ConfirmationDialog from "@au/core/lib/components/objects/ConfirmationDialog";
import { createResponseAlertMessage } from '@au/core/lib/components/objects/AlertMessage';
import { history as browserHistory} from "../../../history";
import EmbeddedDeviceLookup from "./EmbeddedDeviceLookup";
import DefaultList from "../List";
import { deviceType } from "../../../constants";
import { formatMessage } from "../../../utils/reactIntl";
import { filtersToQueryParams } from '../../utils/filters';

import customStyles from "../../../css/components/device_view.module.scss";
import moment from 'moment-timezone';

const SEARCH_SECTION_ID = 'device-search-section';

export default class DeviceList extends DefaultList {

  constructor(props) {
    super(props);

    const now = moment();
    const initialDeviceType = this.getDeviceTypeFromUrl();
    const fixedCreateTimeStart = now.clone().subtract(29, 'days').toISOString();
    const fixedCreateTimeEnd = now.toISOString();

    const deviceTypeQueryParams = initialDeviceType ? {
      type: initialDeviceType,
      createTimeStart: fixedCreateTimeStart,
      createTimeEnd: fixedCreateTimeEnd
    } : {};

    this.state = {
      ...this.state,
      selectedDeviceType: initialDeviceType,
      fixedCreateTimeStart,
      fixedCreateTimeEnd,
      deviceTypeQueryParams,
      sidebarFilterQueryParams: {},
      filterQueryParams: deviceTypeQueryParams,
      searchCollapsed: false,
      pendingCollapse: Boolean(initialDeviceType),
      searchToggleKey: 0
    };

    this._loadedCount = 0;

    this.onDeviceTypeChange = this.onDeviceTypeChange.bind(this);
    this.initializeFilters = this.initializeFilters.bind(this);
    this.handleFiltersChange = this.handleFiltersChange.bind(this);
  }

  onListSuccess(resp) {
    super.onListSuccess(resp);

    const items = resp?.items || resp?.data?.items || [];
    const count = items.size ?? items.length ?? 0;
    this._loadedCount = resp?.isFirstPage ? count : this._loadedCount + count;
  }

  componentDidUpdate(prevProps, prevState) {
    super.componentDidUpdate(prevProps, prevState);

    const { pendingCollapse, fetching, showError } = this.state;
    const loadJustFinished = prevState.fetching && !fetching;

    if (!pendingCollapse || !loadJustFinished) return;

    const hasRows = !showError && this._loadedCount > 0;

    this.setState(prev => ({
      pendingCollapse: false,
      searchCollapsed: hasRows ? true : prev.searchCollapsed,
      searchToggleKey: hasRows ? prev.searchToggleKey + 1 : prev.searchToggleKey
    }));
  }

  getDeviceTypeFromUrl() {
    const params = new URLSearchParams(browserHistory.location.search);
    const type = params.get('type');
    return Object.values(deviceType).includes(type) ? type : null;
  }

  syncDeviceTypeToUrl(value) {
    const params = new URLSearchParams(browserHistory.location.search);
    if (value) {
      params.set('type', value);
    } else {
      params.delete('type');
    }
    browserHistory.replace({
      pathname: browserHistory.location.pathname,
      search: params.toString()
    });
  }

  /*
   * Overrides the base List behavior: skip loading any data until the user
   * has explicitly selected a device type from the dropdown above the list.
   */
  async fetchInitialPages(pagesToLoad) {
    if (!this.state.selectedDeviceType) {
      this.setState({ fetching: false, allDataFetched: true });
      return Promise.resolve();
    }
    return super.fetchInitialPages(pagesToLoad);
  }

  getFilteredEntities() {
    if (!this.state.selectedDeviceType) {
      return this.props.entities.filter(() => false);
    }
    return super.getFilteredEntities();
  }

  onDeviceTypeChange(value) {
    const { fixedCreateTimeStart, fixedCreateTimeEnd } = this.state;

    this._loadedCount = 0;

    const deviceTypeQueryParams = {
      type: value,
      createTimeStart: fixedCreateTimeStart,
      createTimeEnd: fixedCreateTimeEnd
    };

    this.setState(prevState => ({
      selectedDeviceType: value,
      deviceTypeQueryParams,
      filterQueryParams: {
        ...prevState.sidebarFilterQueryParams,
        ...deviceTypeQueryParams
      },
      pendingCollapse: true
    }), () => {
      this.syncDeviceTypeToUrl(value);
      this.applyFilters();
    });
  }

  onSearchToggleChange = e =>
      this.setState({ searchCollapsed: !e.target.checked, pendingCollapse: false });

  getDeviceTypeOptions() {
    return Object.values(deviceType).map(type => ({
      val: type,
      displayString: formatMessage({ id: `au.devices.types.${type}` }) || type
    }));
  }

  renderDeviceTypeSelector() {
    const { selectedDeviceType } = this.state;

    return (
      <div className={customStyles.device_type_selector}>
        <label className={customStyles.device_type_label}>
          <AutoIntl displayId="au.devices.deviceType" />
        </label>
        <AuDropDown
          className={customStyles.device_type_dropdown}
          options={this.getDeviceTypeOptions()}
          selection={selectedDeviceType}
          selectOption={this.onDeviceTypeChange}
          placeholderId="au.filters.placeholders.deviceType"
          />
      </div>
    );
  }

  renderSearchSummary() {
    const { selectedDeviceType, searchCollapsed, searchToggleKey } = this.state;
    const typeLabel = formatMessage({ id: `au.devices.types.${selectedDeviceType}` }) || selectedDeviceType;

    return (
      <div className={customStyles.search_summary}>
        <span className={customStyles.search_summary_pill}>
          <AutoIntl displayId="au.devices.deviceType" />: {typeLabel}
        </span>
        <div className={customStyles.search_toggle}>
          <AutoIntl
            displayId="au.vehicles.search.options"
            tag="span"
            className={customStyles.search_toggle_caption}
            />
          <AuToggle
            key={searchToggleKey}
            name="deviceSearchToggle"
            size={TOGGLE_SIZE_SMALL}
            checked={!searchCollapsed}
            onChange={this.onSearchToggleChange}
            />
        </div>
      </div>
    );
  }

  getRowActions({id, canWrite, canDuplicate, permissions}) {
    const actions = super.getRowActions({id, canWrite, canDuplicate, permissions});

    const entity = this.props.entities.get(id);
    if(!entity){
      return actions
    }

    const binded = entity.hasIn(['relations', 'binding']);

    actions.splice(1, 0, wrapActionWithTracking({
      key: `${binded ? 'un' : ''}bind_device`,
      type: BUTTON_TYPE_SECONDARY,
      // className: styles.button,
      displayId: `au.device.${binded ? 'un' : ''}bindDevice`,
      onClick: () => binded ? this.onUnbindBtnClick(id) : this.onBindBtnClick(id)
    }, 'Device', 'List'));

    return actions;
  }

  onBindBtnClick = this.onBindBtnClick.bind(this);
  onBindBtnClick(entityId) {
    //FIXME - MOVE TO linkHelper
    browserHistory.push({
      pathname: this.baseUrl + `/${entityId}/bind`,
      state: {
        prevUrl: this.props.match.url
      }
    });
  }

  onUnbindBtnClick = this.onUnbindBtnClick.bind(this);
  onUnbindBtnClick(entityId) {
    this.setState({showUnbindDialog: true, unbindEntityId: entityId});
  }

  hideUnbindDialog = this.hideUnbindDialog.bind(this);
  hideUnbindDialog() {
    this.setState({showUnbindDialog: false, unbindEntityId: undefined});
  }

  unbindDevice = this.unbindDevice.bind(this);
  unbindDevice() {
    const {endpoint} = this.props;

    endpoint.unbind(this.state.unbindEntityId).then(
        this.onDeviceUnbindSuccess,
        createResponseAlertMessage
    );
  }

  onDeviceUnbindSuccess = this.onDeviceUnbindSuccess.bind(this);
  onDeviceUnbindSuccess() {
    this.list();
    this.setState({showUnbindDialog: false, unbindSucceeded: null});
  }

  initializeFilters(filters, filtersCount) {
    const { entityDef } = this.props;
    if (!entityDef.sidebarFilters) {
      return super.initializeFilters(filters, filtersCount);
    }

    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: {
        ...sidebarFilterQueryParams,
        ...this.state.deviceTypeQueryParams
      },
      filtersCount
    }, () => {
      this.syncDeviceTypeToUrl(this.state.selectedDeviceType);
      this._initFilters.resolve();
    });
  }

  handleFiltersChange(filters, filtersCount) {
    const sidebarFilterQueryParams = filtersToQueryParams(filters);
    this.setState({
      sidebarFilterQueryParams,
      filterQueryParams: {
        ...sidebarFilterQueryParams,
        ...this.state.deviceTypeQueryParams
      },
      filtersCount
    }, () => {
      this.syncDeviceTypeToUrl(this.state.selectedDeviceType);
      this.applyFilters();
    });
  }

  getUpdaterClassName() {
    return this.state.searchCollapsed
        ? customStyles.device_updater_offset_collapsed
        : customStyles.device_updater_offset;
  }

  renderUnbindDialog() {
    const {showUnbindDialog, unbindSucceeded} = this.state;

    if (!showUnbindDialog) {
      return false;
    }

    return (
      <ConfirmationDialog
        key="device_unbind_dialog"
        type="alert"
        titleId="au.device.unbind.title"
        confirmDisplayId="au.device.unbind.confirm"
        className={customStyles.unbind_dialog}
        headerClassName={customStyles.header}
        succeded={unbindSucceeded}
        onConfirm={this.unbindDevice}
        onCancel={this.hideUnbindDialog}
        >
        <AutoIntl displayId="au.device.unbind.message1" tag="div"/>
        <AutoIntl displayId="au.device.unbind.message2" tag="div"/>
      </ConfirmationDialog>
    );
  }

  renderDialogs() {
    return (
      <>
        {super.renderDialogs()}
        {this.renderUnbindDialog()}
      </>
    );
  }

  renderContent() {
    const { selectedDeviceType, searchCollapsed } = this.state;

    return (
      <>
        {selectedDeviceType && this.renderSearchSummary()}
        <div
          id={SEARCH_SECTION_ID}
          className={searchCollapsed ? customStyles.search_section_collapsed : undefined}
          >
          <EmbeddedDeviceLookup
            endpoint={this.props.endpoint}
            entityDef={this.props.entityDef}
            match={this.props.match}
            controlsClassName={customStyles.device_lookup_controls}
            >
            <div className={customStyles.or_divider}>
              <hr className={customStyles.or_divider_line} />
              <AutoIntl displayId="au.vehicles.or" tag="span" className={customStyles.or_divider_text} />
              <hr className={customStyles.or_divider_line} />
            </div>
            <AutoIntl
              displayId="au.devices.deviceTypeBanner"
              tag="p"
              className={customStyles.device_type_hint}
            />
            {this.renderDeviceTypeSelector()}
          </EmbeddedDeviceLookup>
        </div>
        {super.renderContent()}
      </>
    );
  }
}